/**
 * NeoKanban brand logo component
 * Features a stylized Kanban board with AI neural network elements
 */

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  className?: string;
}

const sizes = {
  sm: { icon: 24, text: 'text-lg' },
  md: { icon: 32, text: 'text-xl' },
  lg: { icon: 40, text: 'text-2xl' },
  xl: { icon: 56, text: 'text-3xl' },
};

export function Logo({ size = 'md', showText = true, className = '' }: LogoProps) {
  const { icon, text } = sizes[size];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoIcon size={icon} />
      {showText && (
        <span className={`font-bold tracking-tight ${text}`}>
          <span className="text-primary-600 dark:text-primary-400">
            Neo
          </span>
          <span className="text-neutral-800 dark:text-white">Kanban</span>
        </span>
      )}
    </div>
  );
}

export function LogoMark({ size = 'md', className = '' }: Omit<LogoProps, 'showText'>) {
  return <Logo size={size} showText={false} className={className} />;
}

interface LogoIconProps {
  size?: number;
  className?: string;
}

export function LogoIcon({ size = 32, className = '' }: LogoIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`drop-shadow-lg ${className}`}
    >
      <defs>
        {/* Main gradient background */}
        <linearGradient id="logoBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1a2e" />
          <stop offset="50%" stopColor="#16213e" />
          <stop offset="100%" stopColor="#0f0f23" />
        </linearGradient>

        {/* Card gradients */}
        <linearGradient id="logoCardGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>

        <linearGradient id="logoCardGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#0891b2" />
        </linearGradient>

        <linearGradient id="logoCardGradient3" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>

        {/* AI glow effect */}
        <filter id="logoGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="logoSoftGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="8" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Neural network node gradient */}
        <radialGradient id="logoNodeGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00d4ff" />
          <stop offset="100%" stopColor="#0891b2" />
        </radialGradient>
      </defs>

      {/* Background with rounded corners */}
      <rect x="0" y="0" width="512" height="512" rx="96" ry="96" fill="url(#logoBgGradient)" />

      {/* Subtle circuit pattern in background */}
      <g opacity="0.15" stroke="#00d4ff" strokeWidth="1" fill="none">
        <path d="M80 200 L120 200 L120 280 L160 280" />
        <path d="M352 200 L392 200 L392 280 L432 280" />
        <path d="M200 80 L200 120 L280 120 L280 160" />
        <path d="M200 352 L200 392 L280 392 L280 432" />
        <circle cx="80" cy="200" r="4" fill="#00d4ff" />
        <circle cx="160" cy="280" r="4" fill="#00d4ff" />
        <circle cx="432" cy="280" r="4" fill="#00d4ff" />
        <circle cx="352" cy="200" r="4" fill="#00d4ff" />
      </g>

      {/* Column containers (subtle) */}
      <g opacity="0.3">
        <rect x="72" y="120" width="108" height="272" rx="12" fill="#ffffff" opacity="0.05" />
        <rect x="202" y="120" width="108" height="272" rx="12" fill="#ffffff" opacity="0.05" />
        <rect x="332" y="120" width="108" height="272" rx="12" fill="#ffffff" opacity="0.05" />
      </g>

      {/* Column 1 - To Do (Purple cards) */}
      <g filter="url(#logoGlow)">
        <rect x="84" y="140" width="84" height="56" rx="10" fill="url(#logoCardGradient1)" />
        <rect x="84" y="208" width="84" height="72" rx="10" fill="url(#logoCardGradient1)" opacity="0.85" />
        <rect x="84" y="292" width="84" height="48" rx="10" fill="url(#logoCardGradient1)" opacity="0.7" />
      </g>

      {/* Column 2 - In Progress (Cyan cards) */}
      <g filter="url(#logoGlow)">
        <rect x="214" y="140" width="84" height="64" rx="10" fill="url(#logoCardGradient2)" />
        <rect x="214" y="216" width="84" height="52" rx="10" fill="url(#logoCardGradient2)" opacity="0.85" />
      </g>

      {/* Column 3 - Done (Green cards) */}
      <g filter="url(#logoGlow)">
        <rect x="344" y="140" width="84" height="48" rx="10" fill="url(#logoCardGradient3)" />
        <rect x="344" y="200" width="84" height="64" rx="10" fill="url(#logoCardGradient3)" opacity="0.85" />
        <rect x="344" y="276" width="84" height="56" rx="10" fill="url(#logoCardGradient3)" opacity="0.7" />
      </g>

      {/* Card detail lines (white) */}
      <g fill="#ffffff" opacity="0.6">
        {/* Column 1 lines */}
        <rect x="96" y="156" width="48" height="4" rx="2" />
        <rect x="96" y="168" width="32" height="4" rx="2" />
        <rect x="96" y="224" width="52" height="4" rx="2" />
        <rect x="96" y="236" width="40" height="4" rx="2" />
        <rect x="96" y="248" width="28" height="4" rx="2" />
        <rect x="96" y="308" width="44" height="4" rx="2" />

        {/* Column 2 lines */}
        <rect x="226" y="156" width="52" height="4" rx="2" />
        <rect x="226" y="168" width="36" height="4" rx="2" />
        <rect x="226" y="180" width="24" height="4" rx="2" />
        <rect x="226" y="232" width="48" height="4" rx="2" />
        <rect x="226" y="244" width="32" height="4" rx="2" />

        {/* Column 3 lines */}
        <rect x="356" y="156" width="44" height="4" rx="2" />
        <rect x="356" y="168" width="28" height="4" rx="2" />
        <rect x="356" y="216" width="56" height="4" rx="2" />
        <rect x="356" y="228" width="40" height="4" rx="2" />
        <rect x="356" y="240" width="24" height="4" rx="2" />
        <rect x="356" y="292" width="48" height="4" rx="2" />
        <rect x="356" y="304" width="32" height="4" rx="2" />
      </g>

      {/* AI Neural network overlay */}
      <g filter="url(#logoSoftGlow)">
        {/* Connection lines */}
        <g stroke="#00d4ff" strokeWidth="2" opacity="0.6" fill="none">
          <line x1="126" y1="380" x2="256" y2="420" />
          <line x1="256" y1="420" x2="386" y2="380" />
          <line x1="126" y1="380" x2="386" y2="380" />
          <line x1="256" y1="420" x2="256" y2="460" />
        </g>

        {/* Neural nodes */}
        <circle cx="126" cy="380" r="10" fill="url(#logoNodeGradient)" />
        <circle cx="256" cy="420" r="12" fill="url(#logoNodeGradient)" />
        <circle cx="386" cy="380" r="10" fill="url(#logoNodeGradient)" />
        <circle cx="256" cy="460" r="8" fill="url(#logoNodeGradient)" />

        {/* Inner glow on nodes */}
        <circle cx="126" cy="380" r="4" fill="#ffffff" opacity="0.8" />
        <circle cx="256" cy="420" r="5" fill="#ffffff" opacity="0.8" />
        <circle cx="386" cy="380" r="4" fill="#ffffff" opacity="0.8" />
        <circle cx="256" cy="460" r="3" fill="#ffffff" opacity="0.8" />
      </g>

      {/* Sparkle/AI indicator top right */}
      <g filter="url(#logoGlow)" transform="translate(400, 72)">
        <polygon points="20,0 24,16 40,20 24,24 20,40 16,24 0,20 16,16" fill="#00d4ff" opacity="0.9" />
        <circle cx="20" cy="20" r="4" fill="#ffffff" />
      </g>
    </svg>
  );
}
